/**
 * useNetworksOverview — cross-chain aggregator for the
 * `/networks/compare` page (Phase 2).
 *
 * Pulls the wallet-count for each monitored chain from its own
 * `useXxxMonitorWallets()` hook (which is cheap: localStorage +
 * Supabase sync, no per-chain API calls). The compare page renders
 * one row per chain with this count, letting the user see at a
 * glance which chains they have wallets configured on.
 *
 * **Why no balances yet?** The 9 per-chain *data* hooks
 * (`useAlgorandWallets`, `useCardanoWallets`, …) each kick off
 * 1-N API requests on mount. Calling all 9 from a single
 * comparison page would saturate the network on every visit. A
 * follow-up phase will add a "Load balances" affordance per row
 * (or batch button) so the user pays the network cost only when
 * they want it.
 *
 * Shape kept intentionally flat (one number per chain) so a UI
 * change doesn't require touching this hook.
 */
import {
  useAlgorandMonitorWallets,
  useAxelarMonitorWallets,
  useCardanoMonitorWallets,
  useCronosWallets,
  useHederaMonitorWallets,
  useIcpMonitorWallets,
  useTheGraphMonitorWallets,
  useVechainMonitorWallets,
  useXdcMonitorWallets,
} from './useMonitorWallets';

/** Page-key identifier (matches the App router). */
export type NetworkOverviewKey =
  | 'algorand-monitor'
  | 'axelar-monitor'
  | 'cardano-monitor'
  | 'cronos-monitor'
  | 'hedera-monitor'
  | 'icp-monitor'
  | 'thegraph-monitor'
  | 'vechain-monitor'
  | 'xdc-monitor';

export interface NetworkOverview {
  /** Number of wallet addresses the user has configured for this chain. */
  walletsCount: number;
  /** Wallet addresses themselves — passed to per-chain BalanceCell
   *  projectors when the user opts into the Phase 3 "Load balances" view. */
  wallets: string[];
  /** True while the underlying monitor hook is syncing with Supabase. */
  isSyncing: boolean;
}

export type NetworksOverview = Record<NetworkOverviewKey, NetworkOverview>;

export function useNetworksOverview(): NetworksOverview {
  const algo = useAlgorandMonitorWallets();
  const axl = useAxelarMonitorWallets();
  const ada = useCardanoMonitorWallets();
  const cro = useCronosWallets();
  const hbar = useHederaMonitorWallets();
  const icp = useIcpMonitorWallets();
  const grt = useTheGraphMonitorWallets();
  const vet = useVechainMonitorWallets();
  const xdc = useXdcMonitorWallets();

  return {
    'algorand-monitor': { walletsCount: algo.wallets.length, wallets: algo.wallets, isSyncing: algo.isSyncing },
    'axelar-monitor': { walletsCount: axl.wallets.length, wallets: axl.wallets, isSyncing: axl.isSyncing },
    'cardano-monitor': { walletsCount: ada.wallets.length, wallets: ada.wallets, isSyncing: ada.isSyncing },
    'cronos-monitor': { walletsCount: cro.wallets.length, wallets: cro.wallets, isSyncing: cro.isSyncing },
    'hedera-monitor': { walletsCount: hbar.wallets.length, wallets: hbar.wallets, isSyncing: hbar.isSyncing },
    'icp-monitor': { walletsCount: icp.wallets.length, wallets: icp.wallets, isSyncing: icp.isSyncing },
    'thegraph-monitor': { walletsCount: grt.wallets.length, wallets: grt.wallets, isSyncing: grt.isSyncing },
    'vechain-monitor': { walletsCount: vet.wallets.length, wallets: vet.wallets, isSyncing: vet.isSyncing },
    'xdc-monitor': { walletsCount: xdc.wallets.length, wallets: xdc.wallets, isSyncing: xdc.isSyncing },
  };
}
